import React, { useCallback, useEffect } from 'react';
import { useThree } from '@react-three/fiber';

const mouseEvents = [ 'click', 'onDoubleClick', 'mousedown', 'mouseup', 'mousemove', 'mouseout', 'mouseenter', 'mouseleave', 'mouseover' ];
const touchEvents = [ 'touchstart', 'touchend', 'touchmove', 'touchcancel' ];
const pointerEvents = [ 'pointerdown', 'pointerup', 'pointerleave', 'pointermove', 'pointercancel', 'lostpointercapture' ];

function useForward<T extends Event>(target: HTMLCanvasElement, type: { new(typeName: string, init: T): T }): (e: T) => void
{
  return useCallback((e: T) =>
  {
    if (!type || e.target === target) return;
    const event = new type(e.type, e);
    target.dispatchEvent(event);
  }, [target, type]);
}

export default function ForwardEvents(): JSX.Element
{
  const { gl } = useThree();
  const forwardMouseEvent = useForward(gl.domElement, MouseEvent);
  const forwardTouchEvent = useForward(gl.domElement, window['TouchEvent']);  // need this for FF
  const forwardPointerEvent = useForward(gl.domElement, PointerEvent);

  useEffect(() =>
  {
    mouseEvents.forEach(type => document.addEventListener(type, forwardMouseEvent));
    touchEvents.forEach(type => document.addEventListener(type, forwardTouchEvent));
    pointerEvents.forEach(type => document.addEventListener(type, forwardPointerEvent));

    return () =>
    {
      mouseEvents.forEach(type => document.removeEventListener(type, forwardMouseEvent));
      touchEvents.forEach(type => document.removeEventListener(type, forwardTouchEvent));
      pointerEvents.forEach(type => document.removeEventListener(type, forwardPointerEvent));
    };
  }, [forwardMouseEvent, forwardPointerEvent, forwardTouchEvent]);

  return <></>;
}